import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import * as PIXI from 'pixi.js'
import { CssColours } from '@/static/colours'
import { EffectState } from '@/pixi/filters/types'

interface OldTVUniforms {
  isAudioReactive: boolean,
  overlayColour: [number, number, number],
  redOffsetInit: [number, number],
  greenOffsetInit: [number, number],
  blueOffsetInit: [number, number],
  lineSpeedCoefficient: number,
  audioIntensity: number
}

export class OldTVFilter extends BaseSeratoFilter {
  get uniforms (): OldTVUniforms {
    return super.uniforms as OldTVUniforms
  }

  constructor (effectState: EffectState) {
    const shader = `
    #define lineThicknessCoefficient 500.0
    #define curvatureCoefficient 0.5
    #define brightnessSides 0.6
    #define brightnessCenter 10.0

    // generic default params
    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;
    uniform float timeElapsed;

    // params to tune effects
    uniform vec3 overlayColour;
    uniform float lineSpeedCoefficient;
    uniform float audioIntensity;
    uniform vec2 redOffsetInit;
    uniform vec2 greenOffsetInit;
    uniform vec2 blueOffsetInit;

    // audio uniforms
    uniform bool isAudioReactive;

    // implements rgb shift
    vec3 getColorShiftedTexturePixel(vec2 textureCoord) {
      vec3 color;
      float intensity = isAudioReactive ? audioIntensity : 1.0;

      vec2 rOffset = redOffsetInit * intensity;
      vec2 gOffset = greenOffsetInit * intensity;
      vec2 bOffset = blueOffsetInit * intensity;

      color.r = texture2D(uSampler, vTextureCoord - rOffset).r;
      color.g = texture2D(uSampler, vTextureCoord - gOffset).g;
      color.b = texture2D(uSampler, vTextureCoord - bOffset).b;

      return color;
    }

    // changes the coordinate of the pixel (one axis)
    float getCurvedCoordAxis(float textureAxisCoord, float distanceToCenter) {
      textureAxisCoord -= 0.5;
      textureAxisCoord *= 1.0 + (distanceToCenter * (0.3 * curvatureCoefficient));
      textureAxisCoord += 0.5;

      return textureAxisCoord;
    }

    // changes the coordinates of the pixel to emulate a curved crt screen
    vec2 getCurvedCoord() {
      // default coordinates
      vec2 textureCoord = vTextureCoord;

      // get the distance to the center of the texture
      vec2 distanceToCenter = abs(0.5 - textureCoord);
      distanceToCenter *= distanceToCenter;

      // modify the coordinates to imitate screen curvature
      textureCoord.x = getCurvedCoordAxis(textureCoord.x, distanceToCenter.y);
      textureCoord.y = getCurvedCoordAxis(textureCoord.y, distanceToCenter.x);

      return textureCoord;
    }

    void main(void)
    {
      // get texture coordinates
      vec2 textureCoord = getCurvedCoord();

      vec4 originalPixelColor = texture2D(uSampler, vTextureCoord);
      vec3 color = originalPixelColor.rgb;

      // color shift
      color = getColorShiftedTexturePixel(textureCoord);
      
      // uneven brightness
      color *= brightnessSides + brightnessCenter * textureCoord.x * vTextureCoord.y * (1.0 - textureCoord.x) * (1.0 - textureCoord.y);

      // recolouring
      color *= overlayColour;

      // lines
      float lineVelocity = -1.0 * timeElapsed * lineSpeedCoefficient;
      float lineThickness = 1100.0 - lineThicknessCoefficient;
      float lineOpacity = 0.08;
      color *= 1.0 + lineOpacity * sin(lineVelocity + textureCoord.y * lineThickness);

      gl_FragColor = vec4(color, originalPixelColor.a);
    }`

    const overlayColourRGB = PIXI.utils.hex2rgb(PIXI.utils.string2hex(CssColours.PALE_BLUE))
    const uniforms: OldTVUniforms = {
      isAudioReactive: false,
      overlayColour: [overlayColourRGB[0], overlayColourRGB[1], overlayColourRGB[2]],
      redOffsetInit: [0, 0],
      blueOffsetInit: [0, 0],
      greenOffsetInit: [0, 0],
      lineSpeedCoefficient: 10,
      audioIntensity: 0
    }

    super(
      effectState,
      undefined,
      shader,
      uniforms
    )

    this.updateUniforms()
  }

  updateUniforms () {
    const intensity : number = this.effectState.intensity / 2
    this.uniforms.audioIntensity = this.getAudioIntensityValue()
    this.uniforms.redOffsetInit = [-0.015 - intensity * 0.2, 0]
    this.uniforms.blueOffsetInit = [0.025 + intensity * 0.2, 0]
    this.uniforms.lineSpeedCoefficient = 5 + this.effectState.intensity * 45
    this.uniforms.isAudioReactive = this.effectState.audioReactivityEnabled
  }
}
